// src/components/timetable/modern/ModernTimetableHeader.tsx
//
// Ported from the mobile app's TimetableHeader.tsx. LinearGradient/Animated/
// Ionicons become CSS gradients/transitions/PrimeIcons (`pi pi-*`), matching
// the icon convention already used in TimeSlotsManager.tsx.

import React from 'react';
import {
  C,
  DAYS_OF_WEEK,
  DayOfWeek,
  TimetableTypeOption,
  ViewMode,
  initials,
} from './modernTimetableTypes';

interface ModernTimetableHeaderProps {
  // Banner data
  timetableTypeName:  string;
  sectionCount:       number;
  teacherCount:       number;
  periodCount:        number;
  presentCount:       number;

  // Selectors
  typeOptions:        TimetableTypeOption[];
  typeOptionsLoading: boolean;
  selectedTypeValue:  string;
  onTypeChange:       (v: string) => void;
  viewMode:           ViewMode;
  onViewModeChange:   (m: ViewMode) => void;
  selectedDay:        DayOfWeek;
  onDayChange:        (d: DayOfWeek) => void;

  // Single-teacher controls
  selectedTeacher:    string | null;
  busyCount:          number;
  vacantCount:        number;
  isSelectedPresent:  boolean;
  onOpenPicker:       () => void;

  fadeIn: boolean;
}

const VIEW_MODES_ROW1: { key: ViewMode; label: string; icon: string }[] = [
  { key: 'classes',         label: 'Classes',      icon: 'pi-building'   },
  { key: 'all_teachers',    label: 'All Teachers', icon: 'pi-users'      },
  { key: 'single_teacher',  label: 'My Teacher',   icon: 'pi-user'       },
  { key: 'vacant_teachers', label: 'Vacants',      icon: 'pi-calendar'   },
];

const VIEW_MODES_ROW2: { key: ViewMode; label: string; icon: string }[] = [
  { key: 'arrangements', label: 'Arrangements', icon: 'pi-sitemap' },
];

const ModernTimetableHeader: React.FC<ModernTimetableHeaderProps> = ({
  timetableTypeName, sectionCount, teacherCount, periodCount, presentCount,
  typeOptions, typeOptionsLoading, selectedTypeValue, onTypeChange,
  viewMode, onViewModeChange,
  selectedDay, onDayChange,
  selectedTeacher, busyCount, vacantCount, isSelectedPresent, onOpenPicker,
  fadeIn,
}) => {
  return (
    <>
      <style>{`
        .mtt-fade { opacity: 0; transform: translateY(14px); transition: opacity 0.5s ease, transform 0.5s ease; }
        .mtt-fade.mtt-fade-in { opacity: 1; transform: translateY(0); }

        .mtt-banner {
          border-radius: 14px; padding: 14px 16px;
          background: linear-gradient(135deg, ${C.primary} 0%, ${C.primaryMid} 100%);
          box-shadow: 0 4px 12px rgba(40,85,174,0.25);
          display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
        }
        .mtt-banner-left { display: flex; align-items: center; gap: 10px; min-width: 0; }
        .mtt-banner-icon {
          width: 34px; height: 34px; border-radius: 17px; flex-shrink: 0;
          background: rgba(255,255,255,0.18); display: flex; align-items: center; justify-content: center;
        }
        .mtt-banner-name { color: #fff; font-size: 14px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .mtt-banner-sub { color: rgba(255,255,255,0.7); font-size: 12px; margin-top: 2px; }
        .mtt-banner-stats { display: flex; align-items: center; gap: 10px; background: rgba(255,255,255,0.14); border-radius: 10px; padding: 6px 12px; }
        .mtt-stat { text-align: center; min-width: 44px; }
        .mtt-stat-val { color: #fff; font-size: 13px; font-weight: 700; display: block; }
        .mtt-stat-lbl { color: rgba(255,255,255,0.7); font-size: 10px; }

        .mtt-row { display: flex; background: ${C.cardBg}; border: 1px solid ${C.border}; border-radius: 12px; padding: 4px; gap: 4px; flex-wrap: wrap; }
        .mtt-row.mtt-row-loading { justify-content: center; align-items: center; padding: 12px; color: ${C.textLight}; font-size: 12.5px; }

        .mtt-type-chip {
          flex: 1; min-width: 90px; border: none; cursor: pointer; border-radius: 9px;
          padding: 9px 10px; font-size: 12.5px; font-weight: 600; color: ${C.textSecondary};
          background: transparent; transition: background 0.15s ease, color 0.15s ease;
        }
        .mtt-type-chip.selected {
          color: #fff; background: linear-gradient(90deg, ${C.primary}, ${C.primaryMid});
        }

        .mtt-mode-row { display: flex; background: ${C.cardBg}; border: 1px solid ${C.border}; border-radius: 12px; padding: 5px; gap: 4px; }
        .mtt-mode-chip {
          flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px;
          border: none; cursor: pointer; border-radius: 9px; padding: 9px 8px;
          font-size: 12.5px; font-weight: 600; color: ${C.textSecondary}; background: #F3F4F6;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .mtt-mode-chip.selected { color: #fff; background: ${C.primary}; }

        .mtt-mode-row2 { display: flex; background: rgba(13,148,136,0.06); border: 1px solid rgba(13,148,136,0.2); border-radius: 12px; padding: 5px; gap: 4px; }
        .mtt-mode-chip2 {
          flex: 1; position: relative; display: flex; align-items: center; justify-content: center; gap: 6px;
          border: none; cursor: pointer; border-radius: 9px; padding: 10px 8px;
          font-size: 12.5px; font-weight: 600; color: #0D9488; background: rgba(13,148,136,0.07);
          transition: background 0.15s ease, color 0.15s ease;
        }
        .mtt-mode-chip2.selected { color: #fff; background: #0D9488; }
        .mtt-mode-badge { background: #0D9488; color: #fff; font-size: 9px; font-weight: 700; border-radius: 5px; padding: 1px 5px; margin-left: 2px; }

        .mtt-vacancy-bar {
          display: flex; align-items: center; gap: 14px; background: ${C.cardBg};
          border: 1px solid ${C.border}; border-radius: 12px; padding: 10px 14px; flex-wrap: wrap;
        }
        .mtt-vacancy-stat { display: flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 600; }
        .mtt-vacancy-dot { width: 8px; height: 8px; border-radius: 4px; }
        .mtt-present-badge {
          display: flex; align-items: center; gap: 6px; background: ${C.presentBg};
          border: 1px solid ${C.presentBorder}; border-radius: 8px; padding: 4px 8px;
        }
        .mtt-present-dot { width: 7px; height: 7px; border-radius: 3.5px; background: #F59E0B; animation: mtt-blink 1.6s ease-in-out infinite; }
        @keyframes mtt-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }
        .mtt-change-btn {
          margin-left: auto; display: flex; align-items: center; gap: 4px; background: none; border: none;
          cursor: pointer; color: ${C.primary}; font-size: 12.5px; font-weight: 700;
        }

        .mtt-picker-btn {
          width: 100%; display: flex; align-items: center; gap: 12px; text-align: left;
          background: ${C.cardBg}; border: 1px solid ${C.border}; border-radius: 12px;
          padding: 12px 14px; cursor: pointer;
        }
        .mtt-picker-btn.present { border-color: ${C.presentBorder}; background: rgba(254,243,199,0.5); }
        .mtt-picker-avatar {
          width: 38px; height: 38px; border-radius: 19px; background: ${C.primary};
          display: flex; align-items: center; justify-content: center; color: #fff;
          font-size: 13px; font-weight: 700; flex-shrink: 0;
        }
        .mtt-picker-avatar.present { background: #D97706; }
        .mtt-picker-label { font-size: 11.5px; color: ${C.textLight}; }
        .mtt-picker-value { font-size: 14px; font-weight: 700; color: ${C.textPrimary}; }

        .mtt-day-row { display: flex; background: ${C.cardBg}; border: 1px solid ${C.border}; border-radius: 12px; padding: 6px; gap: 4px; }
        .mtt-day-chip {
          flex: 1; border: none; cursor: pointer; border-radius: 8px; padding: 9px 4px;
          font-size: 12.5px; font-weight: 600; color: ${C.textSecondary}; background: #F3F4F6;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .mtt-day-chip.selected { color: #fff; background: ${C.primary}; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* ── Banner ── */}
        <div className={`mtt-fade ${fadeIn ? 'mtt-fade-in' : ''}`}>
          <div className="mtt-banner">
            <div className="mtt-banner-left" style={{ flex: 1, minWidth: 0 }}>
              <div className="mtt-banner-icon">
                <i className="pi pi-calendar" style={{ color: '#fff', fontSize: 15 }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div className="mtt-banner-name">{timetableTypeName}</div>
                <div className="mtt-banner-sub">{sectionCount} sections · {teacherCount} teachers</div>
              </div>
            </div>
            <div className="mtt-banner-stats">
              {[
                { value: periodCount,  icon: 'pi-clock',         label: 'Periods' },
                { value: presentCount, icon: 'pi-check-circle',  label: 'Present' },
              ].map((st) => (
                <div key={st.label} className="mtt-stat">
                  <i className={`pi ${st.icon}`} style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }} />
                  <span className="mtt-stat-val">{st.value}</span>
                  <span className="mtt-stat-lbl">{st.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Timetable type selector ── */}
        {typeOptions.length > 0 && (
          <div className={`mtt-row mtt-fade ${fadeIn ? 'mtt-fade-in' : ''}`}>
            {typeOptions.map((type) => (
              <button
                key={type.value}
                type="button"
                className={`mtt-type-chip ${selectedTypeValue === type.value ? 'selected' : ''}`}
                onClick={() => onTypeChange(type.value)}
              >
                {type.label}
              </button>
            ))}
          </div>
        )}
        {typeOptions.length === 0 && typeOptionsLoading && (
          <div className={`mtt-row mtt-row-loading mtt-fade ${fadeIn ? 'mtt-fade-in' : ''}`}>
            Loading timetable types...
          </div>
        )}

        {/* ── View mode switcher — row 1 ── */}
        <div className={`mtt-mode-row mtt-fade ${fadeIn ? 'mtt-fade-in' : ''}`}>
          {VIEW_MODES_ROW1.map((m) => {
            const sel = viewMode === m.key;
            return (
              <button
                key={m.key}
                type="button"
                className={`mtt-mode-chip ${sel ? 'selected' : ''}`}
                onClick={() => onViewModeChange(m.key)}
              >
                <i className={`pi ${m.icon}`} style={{ fontSize: 12 }} />
                {m.label}
              </button>
            );
          })}
        </div>

        {/* ── View mode switcher — row 2 (Arrangements) ── */}
        <div className={`mtt-mode-row2 mtt-fade ${fadeIn ? 'mtt-fade-in' : ''}`}>
          {VIEW_MODES_ROW2.map((m) => {
            const sel = viewMode === m.key;
            return (
              <button
                key={m.key}
                type="button"
                className={`mtt-mode-chip2 ${sel ? 'selected' : ''}`}
                onClick={() => onViewModeChange(m.key)}
              >
                <i className={`pi ${m.icon}`} style={{ fontSize: 12 }} />
                {m.label}
                {!sel && <span className="mtt-mode-badge">New</span>}
              </button>
            );
          })}
        </div>

        {/* ── Single teacher: vacancy summary + picker button ── */}
        {viewMode === 'single_teacher' && (
          <div className={`mtt-fade ${fadeIn ? 'mtt-fade-in' : ''}`} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {selectedTeacher && (
              <div className="mtt-vacancy-bar">
                <div className="mtt-vacancy-stat">
                  <div className="mtt-vacancy-dot" style={{ background: C.primary }} />
                  <span style={{ color: C.primary }}>{busyCount} Busy</span>
                </div>
                <div className="mtt-vacancy-stat">
                  <div className="mtt-vacancy-dot" style={{ background: C.vacant }} />
                  <span style={{ color: C.vacant }}>{vacantCount} Vacant</span>
                </div>
                {isSelectedPresent && (
                  <div className="mtt-present-badge">
                    <div className="mtt-present-dot" />
                    <span style={{ color: C.present, fontWeight: 700, fontSize: 12.5 }}>Present Today</span>
                  </div>
                )}
                <button type="button" className="mtt-change-btn" onClick={onOpenPicker}>
                  <i className="pi pi-arrow-right-arrow-left" style={{ fontSize: 11 }} />
                  Change
                </button>
              </div>
            )}
            <button
              type="button"
              className={`mtt-picker-btn ${isSelectedPresent ? 'present' : ''}`}
              onClick={onOpenPicker}
            >
              <div className={`mtt-picker-avatar ${isSelectedPresent ? 'present' : ''}`}>
                {selectedTeacher ? initials(selectedTeacher) : '?'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="mtt-picker-label">Viewing timetable for</div>
                <div className="mtt-picker-value">{selectedTeacher || 'Select a teacher'}</div>
              </div>
              <i className="pi pi-chevron-down" style={{ color: C.textSecondary, fontSize: 13 }} />
            </button>
          </div>
        )}

        {/* ── Day selector ── */}
        <div className={`mtt-day-row mtt-fade ${fadeIn ? 'mtt-fade-in' : ''}`}>
          {DAYS_OF_WEEK.map((day) => (
            <button
              key={day}
              type="button"
              className={`mtt-day-chip ${selectedDay === day ? 'selected' : ''}`}
              onClick={() => onDayChange(day)}
            >
              {day.substring(0, 3)}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default ModernTimetableHeader;